import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FC } from 'react';
import { BarLoader } from 'react-spinners';
import TrashIcon from '@/assets/trash.svg';
import UnlinkIcon from '@/assets/unlink.svg';
import ViewIcon from '@/assets/view.svg';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerProps } from '@/components/ui/drawer';
import { Modal } from '@/components/ui/modal';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { useCloseInvalidDrawer } from '@/hooks/use-close-invalid-drawer';
import { useAssociatedDevicesInCurrentUnit } from '@/hooks/use-devices';
import { useOpenState } from '@/hooks/use-open-state';
import { useUserRole } from '@/hooks/use-user-role';
import {
  useDeleteDeviceMutation,
  useRemoveDeviceFromUnitMutation,
} from '@/redux/api/endpoints/device';
import { composeTableOptions } from '@/table';
import { DeviceAssociatedWithUnit } from '@/types/device';
import {
  getDeviceStatusDisplay,
  getDeviceTypeLabel,
} from '@/utils/device-helpers';
import { ViewDevice } from '../../drawer-views/view-device';

type OpenContext = { device: DeviceAssociatedWithUnit };

type DrawerView = 'view';

type DrawerContext = { view: DrawerView; deviceId?: number };

export const DevicesView: FC = () => {
  const {
    data: devices,
    isLoading: isLoadingDevices,
    isFetching: isFetchingDevices,
  } = useAssociatedDevicesInCurrentUnit();

  const columns: MRT_ColumnDef<DeviceAssociatedWithUnit>[] = [
    {
      id: 'serialNumber',
      accessorFn: ({ meta }) => meta?.serialNumber,
      header: 'Device Serial Number (DSN)',
      size: 150,
    },
    {
      id: 'type',
      accessorFn: ({ type }) => getDeviceTypeLabel(type),
      header: 'Type',
      filterVariant: 'multi-select',
      size: 120,
    },
    {
      id: 'model',
      accessorFn: ({ meta }) => meta?.model,
      header: 'Model',
      size: 150,
      Cell: ({ renderedCellValue }) => renderedCellValue || '-',
    },
    {
      id: 'status',
      accessorFn: (device) => getDeviceStatusDisplay(device).text,
      header: 'Status',
      grow: false,
      size: 100,
      Cell: ({ row }) => {
        const { text, variant } = getDeviceStatusDisplay(row.original);
        return isFetchingDevices ? (
          <BarLoader color={'gray'} />
        ) : (
          <StatusBadge text={text} variant={variant} />
        );
      },
    },
  ];

  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'view' });
  const {
    state: unassociateDeviceConfirmationState,
    open: openUnassociateDeviceConfirmation,
    toggleOpen: toggleUnassignDeviceConfirmationOpen,
  } = useOpenState<Partial<OpenContext>>({});
  const { isPlatformAdmin } = useUserRole();

  const {
    state: deleteDeviceConfirmationState,
    open: openDeleteDeviceConfirmation,
    toggleOpen: toggleDeleteDeviceConfirmationOpen,
  } = useOpenState<{ device: DeviceAssociatedWithUnit | null }>({
    device: null,
  });

  const [removeDeviceFromUni] = useRemoveDeviceFromUnitMutation();
  const [deleteDevice] = useDeleteDeviceMutation();

  const table = useMaterialReactTable({
    ...composeTableOptions<DeviceAssociatedWithUnit>({
      enableRowActions: true,
    }),
    columns,
    enableColumnResizing: true,
    data: devices,
    state: {
      isLoading: isLoadingDevices,
      ...(isLoadingDevices
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<ViewIcon />}
        key="view"
        label="View"
        onClick={() => {
          closeMenu();
          openDrawer({ view: 'view', deviceId: row.original.id });
        }}
        table={table}
      />,
      // TODO: helper for this?
      row.original.unit !== undefined ? (
        <MRT_ActionMenuItem
          icon={<UnlinkIcon />}
          key="unlink"
          label="Unassociate"
          onClick={() => {
            closeMenu();
            openUnassociateDeviceConfirmation({ device: row.original });
          }}
          table={table}
        />
      ) : null,
      isPlatformAdmin ? (
        <MRT_ActionMenuItem
          icon={<TrashIcon />}
          key="delete"
          label="Delete"
          onClick={() => {
            closeMenu();
            openDeleteDeviceConfirmation({ device: row.original });
          }}
          table={table}
        />
      ) : null,
    ],
  });

  const getDrawerTitleProps = (): Pick<DrawerProps, 'title' | 'subtitle'> => {
    switch (drawerState.context.view) {
      case 'view':
        return {
          title: strings.devices.drawer_view.title,
        };
      default:
        return {
          title: '',
        };
    }
  };

  const viewedDevice = devices.find(
    (device) => device.id === drawerState.context.deviceId,
  );

  useCloseInvalidDrawer(
    drawerState.open,
    Boolean(viewedDevice),
    toggleDrawerOpen,
  );

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'view': {
        const device = viewedDevice;
        if (device) {
          return (
            <ViewDevice
              device={device}
              key={device.id}
              onClose={() => toggleDrawerOpen(false)}
              onDelete={
                isPlatformAdmin
                  ? () => openDeleteDeviceConfirmation({ device })
                  : undefined
              }
              onUnlink={() => {
                openUnassociateDeviceConfirmation({ device });
              }}
              unlinkType="unit"
            />
          );
        }
        break;
      }
      default:
        break;
    }

    return null;
  };

  return (
    <>
      <div>
        {/* TODO: use SectionContainerLayout */}
        <SectionHeadingGroup
          className="mb-6"
          subtitle="A list of all the devices associated with this unit."
          title="Associated Devices"
        />
        <MaterialReactTable table={table} />
      </div>
      <Drawer
        {...getDrawerTitleProps()}
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
      >
        {renderDrawerContent()}
      </Drawer>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleUnassignDeviceConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.devices.modal_confirm_unassociate.cancel}
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              const { device } = unassociateDeviceConfirmationState.context;
              if (device) {
                removeDeviceFromUni({
                  device,
                });
              }
              toggleUnassignDeviceConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.devices.modal_confirm_unassociate.unassociate}
          </Button>,
        ]}
        description={strings.devices.modal_confirm_unassociate.description}
        open={unassociateDeviceConfirmationState.open}
        setOpen={toggleUnassignDeviceConfirmationOpen}
        title={strings.devices.modal_confirm_unassociate.title}
      />
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteDeviceConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.devices.modal_confirm_delete.cancel}
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              const device = deleteDeviceConfirmationState.context.device;
              if (device) {
                deleteDevice({ id: device.id });
              }
              toggleDeleteDeviceConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.devices.modal_confirm_delete.delete}
          </Button>,
        ]}
        description={strings.devices.modal_confirm_delete.description}
        open={deleteDeviceConfirmationState.open}
        setOpen={toggleDeleteDeviceConfirmationOpen}
        title={strings.devices.modal_confirm_delete.title}
      />
    </>
  );
};
